import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonFiltersRow,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonTable,
    SkeletonTableRow,
    SkeletonTabs
} from 'src/components/widgets/skeleton';

const OrdersHeaderActionsSkeleton = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const OrdersHeaderDividerSkeleton = styled.div`
    width: 1px;
    height: 24px;
    background: #e5e7eb;
    margin: 0 4px;
`;

const ordersTableColumns =
    '45px minmax(160px, 1.5fr) minmax(90px, 0.7fr) minmax(90px, 1fr) minmax(90px, 1fr) minmax(90px, 1fr) minmax(120px, 1.2fr) minmax(96px, auto) 60px';

export const OrdersTableRowsSkeleton: FC<{ rows?: number }> = ({ rows = 8 }) => (
    <SkeletonTable aria-hidden='true'>
        <SkeletonTableRow $columns={ordersTableColumns} $padding='9px 12px' $minHeight='36px' $hideLastBorder={false}>
            <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
            <SkeletonBlock $width='44px' $height='12px' />
            <SkeletonBlock $width='58px' $height='12px' />
            <SkeletonBlock $width='44px' $height='12px' />
            <SkeletonBlock $width='76px' $height='12px' />
            <SkeletonBlock $width='54px' $height='12px' />
            <SkeletonBlock $width='42px' $height='12px' />
            <SkeletonBlock $width='1px' $height='12px' />
            <SkeletonBlock $width='1px' $height='12px' />
        </SkeletonTableRow>
        {Array.from({ length: rows }).map((_, index) => (
            <SkeletonTableRow key={index} $columns={ordersTableColumns} $padding='6px 12px' $minHeight='56px'>
                <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                <SkeletonBlock $width={index % 2 === 0 ? '78%' : '62%'} $height='14px' />
                <SkeletonBlock $width={index % 2 === 0 ? '68%' : '52%'} $height='14px' />
                <SkeletonBlock $width='24px' $height='24px' $round />
                <SkeletonBlock $width={index % 3 === 0 ? '48px' : '42px'} $height='22px' $radius='11px' />
                <SkeletonBlock $width='64px' $height='42px' $radius='4px' />
                <SkeletonBlock $width='70%' $height='13px' />
                <SkeletonRow $gap='8px'>
                    <SkeletonBlock $width='32px' $height='32px' $radius='4px' />
                    <SkeletonBlock $width='32px' $height='32px' $radius='4px' />
                    <SkeletonBlock $width='32px' $height='32px' $radius='4px' />
                </SkeletonRow>
                <SkeletonBlock $width='22px' $height='22px' $radius='4px' />
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

export const OrdersListSkeleton: FC<{ rows?: number }> = ({ rows = 6 }) => (
    <>
        <SkeletonFiltersRow
            searchWidth='260px'
            controlWidths={['142px', '142px', '32px', '132px']}
            controlLabelWidths={['58px', '48px', null, null]}
            controlHeights={['38px', '38px', '32px', '38px']}
        />
        <OrdersTableRowsSkeleton rows={rows} />
    </>
);

export const OrdersPageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='180px' $height='26px' />
                <SkeletonBlock $width='min(420px, 100%)' $height='13px' />
            </SkeletonStack>
            <OrdersHeaderActionsSkeleton>
                <SkeletonBlock $width='176px' $height='36px' $radius='6px' />
                <OrdersHeaderDividerSkeleton />
                <SkeletonBlock $width='36px' $height='36px' $radius='6px' />
                <SkeletonBlock $width='36px' $height='36px' $radius='6px' />
            </OrdersHeaderActionsSkeleton>
        </SkeletonPageHeader>
        <SkeletonTabs tabWidths={['84px', '72px', '96px', '118px']} showOverflow />
        <OrdersListSkeleton rows={9} />
    </SkeletonPageShell>
);
